import React, { FC, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { updateModule1ReportReviewNote } from '../core/_requests'

interface ActionReviewNotePopoverProps {
  reportId: number
  reportName?: string
  initialReviewNote?: string | null
  canEdit?: boolean
  onReviewNoteUpdated?: (newReviewNote: string) => void
}

export const ActionReviewNotePopover: FC<ActionReviewNotePopoverProps> = ({
  reportId,
  reportName = 'Month End Report',
  initialReviewNote,
  canEdit = true,
  onReviewNoteUpdated,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [reviewNote, setReviewNote] = useState(initialReviewNote || '')
  const [text, setText] = useState(initialReviewNote || '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setReviewNote(initialReviewNote || '')
    setText(initialReviewNote || '')
  }, [initialReviewNote])

  const hasReviewNote = Boolean(reviewNote && reviewNote.trim())

  const handleOpen = (editMode = false) => {
    setText(reviewNote)
    setIsEditing(editMode || !hasReviewNote)
    setIsOpen(true)
  }

  const handleClose = () => {
    if (isSaving) return
    setIsEditing(false)
    setText(reviewNote)
    setIsOpen(false)
  }

  const handleSave = async () => {
    const trimmed = text.trim()
    setIsSaving(true)
    try {
      await updateModule1ReportReviewNote(reportId, trimmed)
      setReviewNote(trimmed)
      setText(trimmed)
      setIsEditing(false)
      if (onReviewNoteUpdated) {
        onReviewNoteUpdated(trimmed)
      }
      toast.success(trimmed ? 'Review note saved successfully' : 'Review note cleared')
      if (!trimmed) {
        setIsOpen(false)
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save review note')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to clear this review note?')) return
    setIsSaving(true)
    try {
      await updateModule1ReportReviewNote(reportId, '')
      setReviewNote('')
      setText('')
      setIsEditing(false)
      if (onReviewNoteUpdated) {
        onReviewNoteUpdated('')
      }
      toast.success('Review note cleared successfully')
      setIsOpen(false)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to clear review note')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopy = () => {
    if (!reviewNote) return
    navigator.clipboard.writeText(reviewNote)
    toast.success('Review note copied to clipboard', { autoClose: 2000 })
  }

  return (
    <>
      {/* Action Button */}
      {hasReviewNote ? (
        <button
          type='button'
          className='btn btn-icon btn-sm me-1 position-relative'
          style={{
            backgroundColor: '#FFF8DD',
            color: '#F1BC00',
            borderRadius: '6px',
            width: '32px',
            height: '32px',
          }}
          title={`Show/Edit Review Note: ${reviewNote.length > 60 ? reviewNote.substring(0, 60) + '...' : reviewNote}`}
          onClick={() => handleOpen(false)}
        >
          <i className='bi bi-journal-text fs-6 text-warning'></i>
          <span
            className='position-absolute top-0 start-100 translate-middle p-1 bg-warning border border-light rounded-circle'
            style={{ width: '7px', height: '7px' }}
          ></span>
        </button>
      ) : (
        <button
          type='button'
          className='btn btn-icon btn-sm me-1'
          style={{
            borderRadius: '6px',
            width: '32px',
            height: '32px',
          }}
          title='Add Review Note'
          onClick={() => handleOpen(true)}
          disabled={!canEdit}
        >
          <i className='bi bi-journal-plus fs-6 text-muted'></i>
        </button>
      )}

      {/* Modal / Popover Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(1px)',
          }}
          onClick={handleClose}
        >
          <div
            className='card shadow-lg border-0'
            style={{
              width: '92vw',
              maxWidth: '500px',
              borderRadius: '12px',
              overflow: 'hidden',
              animation: 'fadeIn 0.15s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className='card-header border-bottom px-5 py-3 d-flex align-items-center justify-content-between bg-light-subtle'>
              <div className='d-flex align-items-center gap-2'>
                <div
                  className='d-flex align-items-center justify-content-center rounded-circle bg-light-warning text-warning'
                  style={{ width: '34px', height: '34px' }}
                >
                  <i className='bi bi-journal-bookmark-fill fs-5 text-warning'></i>
                </div>
                <div>
                  <h6 className='fw-bold m-0 text-dark'>Review Note</h6>
                  <span
                    className='text-muted fs-8 d-block text-truncate'
                    style={{ maxWidth: '340px' }}
                    title={reportName}
                  >
                    {reportName}
                  </span>
                </div>
              </div>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-light'
                onClick={handleClose}
                aria-label='Close'
                disabled={isSaving}
              >
                <i className='bi bi-x-lg fs-7'></i>
              </button>
            </div>

            {/* Content */}
            <div className='card-body p-5'>
              {!isEditing && hasReviewNote ? (
                /* SHOW / VIEW MODE */
                <div>
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <span className='fs-8 text-muted fw-semibold text-uppercase'>
                      Review Note Content
                    </span>
                    <span className='badge badge-light-warning fs-8 text-warning fw-bold'>
                      {reviewNote.length} chars
                    </span>
                  </div>

                  <div
                    className='p-4 bg-light rounded border text-dark fs-7'
                    style={{
                      whiteSpace: 'pre-wrap',
                      maxHeight: '260px',
                      overflowY: 'auto',
                      lineHeight: '1.6',
                      wordBreak: 'break-word',
                    }}
                  >
                    {reviewNote}
                  </div>

                  {/* Actions Toolbar */}
                  <div className='d-flex align-items-center justify-content-between mt-4 pt-2 border-top'>
                    <div className='d-flex align-items-center gap-2'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light d-flex align-items-center gap-1 py-2 px-3'
                        onClick={handleCopy}
                        title='Copy review note'
                      >
                        <i className='bi bi-clipboard fs-7 text-gray-600'></i>
                        <span>Copy</span>
                      </button>
                      {canEdit && (
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger d-flex align-items-center gap-1 py-2 px-3'
                          onClick={handleClear}
                          disabled={isSaving}
                          title='Clear review note'
                        >
                          <i className='bi bi-trash fs-7'></i>
                          <span>Clear</span>
                        </button>
                      )}
                    </div>

                    {canEdit && (
                      <button
                        type='button'
                        className='btn btn-sm btn-warning d-flex align-items-center gap-1 py-2 px-3 text-dark'
                        onClick={() => {
                          setText(reviewNote)
                          setIsEditing(true)
                        }}
                        title='Edit review note'
                      >
                        <i className='bi bi-pencil-square fs-7 text-dark'></i>
                        <span className='fw-semibold'>Edit</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* ADD / EDIT MODE */
                <div>
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <label className='form-label fw-bold fs-7 text-dark m-0'>
                      {hasReviewNote ? 'Edit Review Note' : 'Add Review Note'}
                    </label>
                    <span className='fs-8 text-muted'>
                      {text.length} / 1000
                    </span>
                  </div>

                  <textarea
                    className='form-control form-control-solid fs-7 p-3'
                    rows={5}
                    maxLength={1000}
                    placeholder='Type your review note for this month-end report here...'
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoFocus
                    disabled={isSaving}
                    style={{
                      resize: 'vertical',
                      lineHeight: '1.5',
                      border: '1px solid #E1E3EA',
                    }}
                  />

                  {/* Edit Actions Toolbar */}
                  <div className='d-flex align-items-center justify-content-end gap-2 mt-4 pt-2 border-top'>
                    {hasReviewNote && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light d-flex align-items-center gap-1 py-2 px-3 me-auto'
                        onClick={() => {
                          setText(reviewNote)
                          setIsEditing(false)
                        }}
                        disabled={isSaving}
                        title='Back to view'
                      >
                        <i className='bi bi-arrow-left fs-7'></i>
                        <span>Show</span>
                      </button>
                    )}
                    <button
                      type='button'
                      className='btn btn-sm btn-light d-flex align-items-center gap-1 py-2 px-3'
                      onClick={handleClose}
                      disabled={isSaving}
                      title='Cancel'
                    >
                      <i className='bi bi-x-lg fs-7'></i>
                      <span>Cancel</span>
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-warning d-flex align-items-center gap-1 py-2 px-4 text-dark'
                      onClick={handleSave}
                      disabled={isSaving}
                      title='Save review note'
                    >
                      {isSaving ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-1' />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <i className='bi bi-check2 fs-6 text-dark'></i>
                          <span className='fw-bold'>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ActionReviewNotePopover
